<template>
  <!-- C-L1 (XOL-596 approval lock UX): the prompt that asks whether to LOCK an
       approval: on a drawer close, on Complete Step, on a PRG Save, and (unlock
       mode) before an Unlock. A small dedicated dialog, not $msgbox: a MessageBox has
       two buttons, its Esc used to mean "close" rather than "cancel" (here Esc must mean
       Stay), and it is one shared instance with a queue (the 15 s hang dialog could
       queue behind it). Esc (XOL-629): with focus inside, onKey answers Stay; with focus
       on body Element's own window listener calls before-close -> onCancel, so it
       answers Stay wherever focus is. LayerHost's capture-phase record of the modal
       stack (XOL-609) keeps the key from also closing the drawer behind.
       Mounted imperatively by lib/approvalLock (askLock / askUnlock), which resolves
       only AFTER the dialog has closed, so no command starts while it is open. -->
  <el-dialog
    ref="dlg"
    :visible.sync="open"
    :title="title"
    :width="isMediumOrLess ? '92%' : '480px'"
    custom-class="xol-lock-dialog"
    :close-on-click-modal="false"
    :close-on-press-escape="true"
    :show-close="true"
    :before-close="onCancel"
    @opened="onOpened"
    @closed="settle"
    @keydown.native="onKey">

    <div id="xol-lock-dialog-body" class="lock-body">
      <i class="el-icon-warning lock-body__icon" aria-hidden="true"></i>
      <div class="lock-body__text">

        <template v-if="mode === 'close'">
          <p>{{lead}}</p>
          <ul class="lock-rows">
            <li v-for="(r, i) in rowTexts" :key="i"><b>{{r.head}}</b>{{r.tail}}</li>
          </ul>
          <p>{{explain}}</p>
        </template>

        <template v-else-if="mode === 'complete'">
          <p>{{completeBody}}</p>
          <p>{{completeNote}}</p>
        </template>

        <p v-else-if="mode === 'prgSave'">{{prgBody}}</p>

        <p v-else>{{unlockBody}}</p>

      </div>
    </div>

    <div slot="footer" class="lock-footer">
      <el-button ref="cancel" size="small" @click="choose('cancel')">{{cancelLabel}}</el-button>
      <span class="lock-footer__right">
        <el-button v-if="mode !== 'unlock'" ref="skip" size="small" @click="choose('skip')">{{skipLabel}}</el-button>
        <el-button ref="primary" size="small" type="primary" @click="choose(mode === 'unlock' ? 'unlock' : 'lock')">{{primaryLabel}}</el-button>
      </span>
    </div>

  </el-dialog>
</template>

<script>
import { Dialog, Button } from 'element-ui'
import { copyText } from '@/lib/requestSyncCopy'

// While open, how often to check that what the prompt is about still exists (a
// destroyed drawer closes the prompt as Stay).
export var ALIVE_POLL_MS = 500

export default {
  name: 'approval-lock-dialog',

  components: { ElDialog: Dialog, ElButton: Button },

  props: {
    // 'close' | 'complete' | 'prgSave' | 'unlock'
    mode: {
      type: String,
      required: true
    },
    // close mode: [{ n, title, decision, date }]
    rows: {
      type: Array,
      default: function () { return [] }
    },
    rtx: {
      type: String,
      default: ''
    },
    id: {
      type: [Number, String],
      default: null
    },
    // complete mode: the decision named in the note
    decision: {
      type: String,
      default: ''
    },
    // complete mode: the step number the body names
    stepNo: {
      type: [Number, String],
      default: null
    },
    // unlock mode: { n, title, decision, fields }
    unlock: {
      type: Object,
      default: null
    },
    // false once the thing the prompt is about is gone
    alive: {
      type: Function,
      default: null
    }
  },

  data () {
    return {
      open: true,
      result: null,
      opener: null,
      watchTimer: null
    }
  },

  computed: {
    n () { return this.rows.length },

    title () {
      switch (this.mode) {
        case 'complete': return copyText('requestSync.approvalLock.complete.title')
        case 'prgSave': return copyText('requestSync.approvalLock.prg.title')
        case 'unlock': return copyText('requestSync.approvalLock.unlock.title', this.unlock || {})
        default: return copyText('requestSync.approvalLock.ask.title', { n: this.n })
      }
    },

    lead () { return copyText('requestSync.approvalLock.ask.lead', { n: this.n, rtx: this.rtx, id: this.id }) },
    explain () { return copyText('requestSync.approvalLock.ask.explain') },
    completeBody () { return copyText('requestSync.approvalLock.complete.body', { n: this.stepNo }) },
    completeNote () { return copyText('requestSync.approvalLock.complete.lockNote', { decision: this.decision }) },
    prgBody () { return copyText('requestSync.approvalLock.prg.body') },
    unlockBody () { return copyText('requestSync.approvalLock.unlock.body', this.unlock || {}) },

    // "Step 2: Technical Proposal" in bold, then " — Approved, 13 Oct 2026".
    rowTexts () {
      return this.rows.map(function (r) {
        var text = copyText('requestSync.approvalLock.ask.row', r)
        var at = text.indexOf(' — ')

        return at === -1 ? { head: text, tail: '' } : { head: text.slice(0, at), tail: text.slice(at) }
      })
    },

    cancelLabel () {
      switch (this.mode) {
        case 'complete': return copyText('requestSync.approvalLock.complete.cancel')
        case 'prgSave': return copyText('requestSync.approvalLock.prg.cancel')
        case 'unlock': return copyText('requestSync.approvalLock.unlock.cancel')
        default: return copyText('requestSync.approvalLock.ask.stay')
      }
    },

    skipLabel () {
      switch (this.mode) {
        case 'complete': return copyText('requestSync.approvalLock.complete.skip')
        case 'prgSave': return copyText('requestSync.approvalLock.prg.skip')
        default: return copyText('requestSync.approvalLock.ask.skip')
      }
    },

    primaryLabel () {
      switch (this.mode) {
        case 'complete': return copyText('requestSync.approvalLock.complete.lock')
        case 'prgSave': return copyText('requestSync.approvalLock.prg.lock')
        case 'unlock': return copyText('requestSync.approvalLock.unlock.ok')
        default: return copyText('requestSync.approvalLock.ask.lock', { n: this.n })
      }
    }
  },

  created () {
    this.opener = typeof document !== 'undefined' ? document.activeElement : null

    if (typeof this.alive === 'function') {
      var self = this
      this.watchTimer = setInterval(function () {
        if (!self.alive()) {
          self.choose('cancel')
        }
      }, ALIVE_POLL_MS)
    }
  },

  mounted () {
    // Focus does not wait for Element's `opened` (the end of the open transition): the
    // user can press Esc or Enter the moment the prompt appears.
    this.$nextTick(this.onOpened)
  },

  beforeDestroy () {
    clearInterval(this.watchTimer)
  },

  methods: {
    // Closes the dialog; the answer is handed over by `settle`, after it has closed.
    choose (result) {
      if (this.result === null) {
        this.result = result
      }
      this.open = false
    },

    // The X (and Esc, below): Stay / Cancel, never "Leave unlocked".
    onCancel () {
      this.choose('cancel')
    },

    // Focus: the primary (locking is encouraged and reversible); the unlock confirm
    // focuses Cancel because it removes protection.
    onOpened () {
      var ref = this.mode === 'unlock' ? this.$refs.cancel : this.$refs.primary

      if (ref && ref.$el && typeof ref.$el.focus === 'function') {
        ref.$el.focus()
      }

      var dialog = this.$refs.dlg && this.$refs.dlg.$refs.dialog

      if (dialog && typeof dialog.setAttribute === 'function') {
        dialog.setAttribute('aria-describedby', 'xol-lock-dialog-body')
      }
    },

    // Esc = Stay. With focus inside, stop the key here so Element's window listener does
    // not also fire (choose() is idempotent anyway). With focus on body Element's listener
    // answers it through before-close (XOL-629). Tab stays inside the dialog.
    onKey (event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.stopPropagation()
        event.preventDefault()
        this.onCancel()
        return
      }

      if (event.key !== 'Tab') {
        return
      }

      var root = this.$refs.dlg && this.$refs.dlg.$el
      var box = root && typeof root.querySelector === 'function' ? root.querySelector('.el-dialog') : null

      if (!box) {
        return
      }

      var items = Array.prototype.slice.call(box.querySelectorAll('button:not([disabled])'))

      if (items.length === 0) {
        return
      }

      var first = items[0]
      var last = items[items.length - 1]
      var active = document.activeElement

      if (event.shiftKey && (active === first || !box.contains(active))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (active === last || !box.contains(active))) {
        event.preventDefault()
        first.focus()
      }
    },

    // The dialog has closed: give focus back and hand the answer over.
    settle () {
      clearInterval(this.watchTimer)

      if (this.opener && typeof this.opener.focus === 'function' && document.body.contains(this.opener)) {
        this.opener.focus()
      }

      this.$emit('settled', this.result === null ? 'cancel' : this.result)
    }
  }
}
</script>

<style lang="scss" scoped>
.lock-body {
  display: flex;
  gap: 12px;
  font-size: 14px;
  color: #606266;
  line-height: 1.5;
}

.lock-body__icon {
  flex: none;
  font-size: 24px;
  color: #E6A23C;
}

.lock-body__text {
  flex: 1;
  min-width: 0;

  p {
    margin: 0 0 8px;
  }
}

.lock-rows {
  margin: 0 0 8px;
  padding-left: 18px;

  b {
    color: #303133;
  }
}

.lock-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.lock-footer__right {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 8px;

  ::v-deep .el-button + .el-button {
    margin-left: 0;
  }
}

@media (max-width: 479px) {
  .lock-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .lock-footer__right {
    flex-direction: column;
    align-items: stretch;
  }

  .lock-footer ::v-deep .el-button + .el-button {
    margin-left: 0;
  }
}
</style>

<style>
/* Element's theme sets `word-break: break-all` on `.el-dialog__body`, which broke the
   text mid-word ("e ditable", "an d") at any width. Not scoped: `.xol-lock-dialog` is
   the class of Element's own inner `.el-dialog` element. Long step names and ids still
   wrap, at a word boundary or, failing that, anywhere. */
.xol-lock-dialog .el-dialog__body {
  word-break: normal;
  overflow-wrap: break-word;
}
.xol-lock-dialog {
  word-break: normal;
  overflow-wrap: break-word;
}
</style>
